import Component from '@glimmer/component';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { formatSuccessRate } from 'garaje/components/health-dashboard/format-success-rate';
import UiCard from 'garaje/components/ui/card';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';

function formatRelativeTime(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

interface Integration {
  successRate?: number | null;
  successRate24h?: number | null;
  lastSeenAt?: string | null;
  activeOperationTypes?: string[];
  totalOperations?: number;
  totalEvents?: number;
}

interface StatCard {
  label: string;
  value: string;
  rangedValue: string;
  trend: string | null;
  deltaDisplay: string | null;
  deltaPositive: boolean | null;
  rangeLabel: string;
  cardDescription: string;
}

interface HealthResponse {
  integrations: Integration[];
  operationsSent?: number;
  operationsReceived?: number;
  accessEventsReceived?: number;
}

interface StatsCardsSignature {
  Args: {
    isLoading?: boolean;
    previousRangeIntegrations: Integration[];
    currentRangeIntegrations: Integration[];
    previousRangeHealth?: HealthResponse;
    currentRangeHealth?: HealthResponse;
    selectedTimeRange: string;
  };
}

export default class StatsCards extends Component<StatsCardsSignature> {
  get stats(): StatCard[] {
    const currentRangeIntegrations = this.args.currentRangeIntegrations ?? [];
    const previousRangeIntegrations = this.args.previousRangeIntegrations ?? [];
    const { selectedTimeRange } = this.args;

    const currentRange = currentRangeIntegrations;
    const previousRange = previousRangeIntegrations;

    const pctChange = (currentVal: number, previousVal: number) =>
      previousVal !== 0 ? `${((Math.abs(currentVal - previousVal) / Math.abs(previousVal)) * 100).toFixed(1)}%` : null;

    const makeStat = (
      label: string,
      currentVal: number | null,
      previousVal: number | null,
      formatVal: (v: number) => string,
      cardDescription: string,
      showDelta = true,
    ): StatCard => {
      let trend = null;
      let deltaDisplay = null;
      let deltaPositive = null;
      if (currentVal != null && previousVal != null) {
        if (currentVal > previousVal) trend = 'higher';
        else if (currentVal < previousVal) trend = 'lower';
        else trend = 'same';
        deltaDisplay = showDelta ? pctChange(currentVal, previousVal) : null;
        deltaPositive = currentVal >= previousVal;
      }
      return {
        label,
        value: previousVal != null ? formatVal(previousVal) : formatVal(0),
        rangedValue: currentVal != null ? formatVal(currentVal) : formatVal(0),
        trend,
        deltaDisplay,
        deltaPositive,
        rangeLabel: selectedTimeRange,
        cardDescription: showDelta ? `${cardDescription}. % change vs previous ${selectedTimeRange}.` : cardDescription,
      };
    };

    // Integration-derived cards (require per-install data)
    const integrationStats: StatCard[] = [];

    const avgSuccessRate = (list: Integration[]) =>
      list.length > 0 ? list.reduce((s, i) => s + (i.successRate ?? i.successRate24h ?? 0), 0) / list.length : null;

    const latestMs = (list: Integration[]) => {
      const ts = list.map((i) => i.lastSeenAt).filter(Boolean) as string[];
      return ts.length ? Math.max(...ts.map((t) => new Date(t).getTime())) : null;
    };

    const opTypesCount = (list: Integration[]) => new Set(list.flatMap((i) => i.activeOperationTypes ?? [])).size;

    const totalOps = (list: Integration[]) =>
      list.length > 0 ? list.reduce((s, i) => s + (i.totalOperations ?? i.totalEvents ?? 0), 0) : null;

    const currentLastSeenMs = latestMs(currentRange);
    const previousLastSeenMs = latestMs(previousRange);
    const lastSeenTrend = null;
    const lastSeenDelta = null;
    const lastSeenDeltaPositive = null;

    integrationStats.push(
      makeStat(
        'Success rate',
        avgSuccessRate(currentRange),
        avgSuccessRate(previousRange),
        (v) => `${formatSuccessRate(v)}%`,
        'Average success rate across all selected integrations for this period',
      ),
      {
        label: 'Last seen',
        value: previousLastSeenMs != null ? formatRelativeTime(previousLastSeenMs) : 'N/A',
        rangedValue: currentLastSeenMs != null ? formatRelativeTime(currentLastSeenMs) : 'N/A',
        trend: lastSeenTrend,
        deltaDisplay: lastSeenDelta,
        deltaPositive: lastSeenDeltaPositive,
        rangeLabel: selectedTimeRange,
        cardDescription: 'Most recent event timestamp across all selected integrations',
      },
      makeStat(
        'Active ops',
        opTypesCount(currentRange),
        opTypesCount(previousRange),
        (v) => String(v),
        'Number of distinct operation types that produced at least one event',
      ),
      makeStat(
        'Total events',
        totalOps(currentRange),
        totalOps(previousRange),
        (v) => v.toLocaleString(),
        'Total event count across all selected integrations',
      ),
    );

    // Health-response-level cards (independent of per-install integrations array)
    return [
      ...integrationStats,
      makeStat(
        'Operations sent',
        this.args.currentRangeHealth?.operationsSent ?? null,
        this.args.previousRangeHealth?.operationsSent ?? null,
        (v) => v.toLocaleString(),
        'Total operations sent from Envoy to the integration',
      ),
      makeStat(
        'Operations received',
        this.args.currentRangeHealth?.operationsReceived ?? null,
        this.args.previousRangeHealth?.operationsReceived ?? null,
        (v) => v.toLocaleString(),
        'Total operations received by Envoy from the integration',
      ),
      makeStat(
        'Access events',
        this.args.currentRangeHealth?.accessEventsReceived ?? null,
        this.args.previousRangeHealth?.accessEventsReceived ?? null,
        (v) => v.toLocaleString(),
        'Total access events received during this period',
      ),
    ];
  }

  <template>
    <div class="relative">
      {{#if @isLoading}}
        <div class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <LoadingSpinnerPulse />
        </div>
      {{/if}}
      <div class="stats-cards-grid mb-8">
        {{#each this.stats as |stat|}}
          <UiCard class="p-4 relative">
            <span class="absolute top-2 right-2 cursor-default" aria-label="More information">
              <img src="/assets/images/gray-info.svg" alt="info" class="w-3.5 h-3.5" />
              <EmberTooltip @side="top" @text={{stat.cardDescription}} @popperContainer=".ui__page" />
            </span>
            <div class="text-xl font-bold">{{stat.rangedValue}}</div>
            <div class="text-xs text-carbon-50 mt-1">{{stat.label}}</div>
            {{#if stat.deltaDisplay}}
              <div class="flex items-center gap-1.5 mt-2">
                {{#if stat.deltaPositive}}
                  <div
                    class="rounded-full bg-cilantro-5 w-5 h-5 flex items-center justify-center shrink-0 overflow-hidden"
                  >
                    {{svgJar "carot-up" class="w-3 h-3 shrink-0 icon-fill-cilantro-50"}}
                  </div>
                  <span class="text-xs font-medium text-cilantro-50">{{stat.deltaDisplay}}</span>
                {{else}}
                  <div class="rounded-full bg-red-40 w-5 h-5 flex items-center justify-center shrink-0 overflow-hidden">
                    {{svgJar "carot-down" class="w-3 h-3 shrink-0 icon-fill-white"}}
                  </div>
                  <span class="text-xs font-medium text-red-40">{{stat.deltaDisplay}}</span>
                {{/if}}
              </div>
            {{/if}}
          </UiCard>
        {{/each}}
      </div>
    </div>
  </template>
}
